import { useEffect, useMemo, useState } from "react";

import { DateTime } from "luxon";
import { DateRangeType } from "react-tailwindcss-datepicker";
import { useForm } from "react-hook-form";

import { createColumnHelper, RowModel } from "@tanstack/react-table";

import Modal from "../../layouts/Modal";
import useEbay from "../../data/useEbay";
import PartImage from "../stock/PartImage";
import ClickToCopy from "../shared/ClickToCopy";
import { PageMode } from "../../models/PagedTable";
import { FormatType, IEbayOffer } from "../../models/EbayOffer";
import PagedTable from "../shared/Tables/PagedTable";
import ContactFromId from "../contacts/ContactFromId";
import SelectInput from "../shared/Inputs/SelectInput";
import formatDateTime from "../../utils/formatDateTime";
import { IPart, IPartCategory } from "../../models/Part";
import ConfirmModal from "../shared/Confirm/ConfirmModal";
import usePartMutations from "../../data/usePartMutations";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import { computeEbayListingTitle } from "./EbayListingTitle";
import useNotification from "../notifications/useNotifications";
import UnlistedNpmUpdate from "./unlistedTable/UnlistedMpnUpdate";
import UnlistedOfferPrice from "./unlistedTable/UnlistedOfferPrice";
import { ListingStatus } from "../../pages/EbayListings/ViewListing";
import TableCheckboxCol from "../shared/Tables/IndeterminateCheckbox";
import UnlistedCategoryRow from "./unlistedTable/UnlistedCategoryRow";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import useBatchListingsMutations from "../../data/useBatchListingsMutations";
import useTemplateProductDescription from "../../data/useTemplateProductDescription";
import BulkEditListingSpecifics from "./BulkEditListingSpecifics";
import DateRangeFilter from "../shared/DateRangeFilter";
import ComboBoxInput from "../shared/Inputs/ComboBoxInput";
import WarehouseLocationSearch from "../warehouseLocations/WarehouseLocationSearch";
import useWarehouseLocations from "../../data/useWarehouseLocations";
import useCategories from "../../data/useCategories";
import { useQueryClient } from "@tanstack/react-query";
import { IWarehouseLocation } from "../../models/Location";
import ListingExpandedRowContent from "./ListingExpandedRowContent";
import SyncEbayListingsModal from "./SyncEbayListingsModal";

const colHelper = createColumnHelper<IPart>();

type LocationSearchMode = "equals" | "beginsWith";

interface IFilterFormFields {
  partCategory: IPartCategory | undefined;
  locationSearchMode: LocationSearchMode;
  warehouseLocation: IWarehouseLocation | undefined;
  warehouseLocationId: string | undefined;
  warehouseLocationBeginsWith: string;
  dateFieldType: string | undefined;
}

const defaultFilterFormValues: IFilterFormFields = {
  partCategory: undefined,
  locationSearchMode: "equals",
  warehouseLocation: undefined,
  warehouseLocationId: undefined,
  warehouseLocationBeginsWith: "",
  dateFieldType: "Created"
};

export default function EbayListingsTable({ categories }: { categories: IPartCategory[] | undefined; }) {
  const { addNotification } = useNotification();
  const { saveHandlers: bulkSaveHandlers } = useDefaultCRUDHandlers("Parts");
  const { bulkUpdateProductDescription } = useTemplateProductDescription();
  const { bulkWithdraw, bulkUpdateOffer } = usePartMutations();
  const { bulkRelist } = useBatchListingsMutations();
  const { warehouseLocations } = useWarehouseLocations();
  const { categories: allCategories } = useCategories();
  const queryClient = useQueryClient();

  const [confirmOpen, setConfirmOpen] = useState<boolean>(false);
  const [confirmFunction, setConfirmFunction] = useState<(() => void) | null>(null);
  const [confirmMessage, setConfirmMessage] = useState<string>("");

  const [policyChangeOpen, setPolicyChangeOpen] = useState<boolean>(false);
  const [aspectsChangeOpen, setAspectsChangeOpen] = useState<boolean>(false);
  const [syncEbayOpen, setSyncEbayOpen] = useState<boolean>(false);
  const [selectedRows, setSelectedRows] = useState<RowModel<IPart> | null>(null);

  const { policies, locations } = useEbay();

  interface IBulkUpdatePolicies {
    locationKey?: string;
    paymentPolicyId?: string;
    fulfillmentPolicyId?: string;
    returnPolicyId?: string;
  }

  const { register, setValue, watch, handleSubmit, reset } = useForm<IBulkUpdatePolicies>({
    defaultValues: {
      locationKey: undefined,
      paymentPolicyId: undefined,
      fulfillmentPolicyId: undefined,
      returnPolicyId: undefined,
    }
  });

  const { register: registerFilters, setValue: setFilterValue, watch: watchFilters } = useForm<IFilterFormFields>({
    defaultValues: defaultFilterFormValues,
  });

  const [dates, setDates] = useState<DateRangeType>({
    startDate: null,
    endDate: null
  });
  const [dateFieldType, setDateFieldType] = useState<string>("Created");
  const [additionalBody, setAdditionalBody] = useState<Record<string, any>>({});
  const [isSearching, setIsSearching] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(false);

  const watchPartCategory = watchFilters("partCategory");
  const watchWarehouseLocation = watchFilters("warehouseLocation");
  const watchLocationSearchMode = watchFilters("locationSearchMode");
  const watchWarehouseLocationBeginsWith = watchFilters("warehouseLocationBeginsWith");

  const cols = useMemo(
    () => [
      TableCheckboxCol(true, true, true, "ebay-listings"),
      colHelper.display({
        id: "images",
        cell: (info) =>
          <PartImage part={info.row.original} width="min-w-10 max-w-32" />
      }),
      colHelper.accessor("categoryId", {
        header: "Category",
        id: "categoryId",
        sortDescFirst: false,
        sortingFn: "auto",
        cell: (info) => <UnlistedCategoryRow part={info.row.original} categories={categories} />,
      }),
      colHelper.accessor("tagNumber", {
        header: "Tag No.",
        id: "tagNumber",
        sortingFn: "auto",
        cell: (info) => <div className="flex flex-col">
          <ClickToCopy text={info.row.original.tagNumber} />
        </div>,
      }),
      colHelper.accessor("offers.listingStarted", {
        id: "listedDate",
        header: "Listed Date",
        sortingFn: "auto",
        cell: (info) => <span>
          {
            info.row.original.offers?.at(0)?.listingStarted ?
              formatDateTime(info.row.original.offers?.at(0)?.listingStarted!, DateTime.DATE_SHORT) :
              "Not Listed"
          }
        </span>,
      }),
      colHelper.accessor("offers", {
        id: "offer title",
        header: "Offer Title",
        sortingFn: "auto",
        cell: (info) => <div className="flex flex-col">
          <span className="text-xs text-gray-600">Current Title:</span>
          <span>{info.getValue()?.at(0)?.title}</span>
          <span className="text-xs text-gray-600 mt-2">Wilma Title:</span>
          <span>{computeEbayListingTitle(info.row.original, null, true)}</span>
        </div>,
      }),
      colHelper.accessor(row => {
        const offer = row.offers?.[0];
        const offerPrice = offer
          ? (offer.format === FormatType.Auction ? offer.auctionStartPrice : offer.buyItNowPrice)
          : null;
        return offerPrice ?? row.enteredPrice ?? 0;
      }, {
        id: "offer.price",
        header: "Offer Price",
        // sortingFn: "auto",
        enableSorting: true,
        cell: (info) => <UnlistedOfferPrice part={info.row.original} />,
      }),
      colHelper.display({
        id: "mpn image",
        header: "MPN",
        cell: (info) => <UnlistedNpmUpdate part={info.row.original} />,
      }),
      colHelper.accessor("offers", {
        id: "policies",
        header: "Policies",
        enableSorting: false,
        cell: (info) => {
          const offer = info.getValue()?.at(0);
          if (!offer) return <span className="text-gray-400">No offer</span>;

          const locationName = locations.data?.data.locations.find(
            (loc) => loc.id === offer.locationKey
          )?.name || offer.locationKey || "Not set";

          const fulfillmentPolicyName = policies.data?.data.fulfilmentPolicies.find(
            (policy) => policy.id === offer.fulfillmentPolicyId
          )?.name || offer.fulfillmentPolicyId || "Not set";

          const paymentPolicyName = policies.data?.data.paymentPolicies.find(
            (policy) => policy.id === offer.paymentPolicyId
          )?.name || offer.paymentPolicyId || "Not set";

          const returnPolicyName = policies.data?.data.returnPolicies.find(
            (policy) => policy.id === offer.returnPolicyId
          )?.name || offer.returnPolicyId || "Not set";

          return (
            <div className="flex flex-col text-xs space-y-1">
              <div><span className="text-gray-600">Location:</span> {locationName}</div>
              <div><span className="text-gray-600">Shipping:</span> {fulfillmentPolicyName}</div>
              <div><span className="text-gray-600">Payment:</span> {paymentPolicyName}</div>
              <div><span className="text-gray-600">Return:</span> {returnPolicyName}</div>
            </div>
          );
        },
      }),
      // colHelper.accessor("offers", {
      //   id: "offer type",
      //   header: "Offer Type",
      //   sortingFn: "auto",
      //   cell: (info) => <span>{FormatType[info.getValue()?.at(0)?.format!]}</span>,
      // }),

      colHelper.accessor("offers", {
        id: "listed by",
        header: "Listed By",
        enableSorting: false,
        cell: (info) => {
          const publishedBy = info.getValue()?.at(0)?.publishedBy;
          return publishedBy ? <ContactFromId contactId={publishedBy} /> : <span>Not Set</span>;
        },
      }),
      colHelper.accessor("offers", {
        id: "offer status",
        header: "Status",
        enableSorting: false,
        cell: (info) => <ListingStatus offer={info.getValue()?.at(0)} part={info.row.original} />,
      }),
    ],
    [categories, policies.data, locations.data]
  );


  const handleBulkResetDescription = (rows: RowModel<IPart>) => {
    const selected = rows.rows.filter((row) => row.getIsSelected());

    if (selected.length === 0) {
      addNotification({
        primaryText: "No parts selected",
        variant: "warn"
      });
      return;
    }

    const partIds = selected.map((row) => row.original.id);

    bulkUpdateProductDescription.mutate({ partIds, updateOffer: true }, bulkSaveHandlers);
  };

  const handleBulkWithdraw = (rows: RowModel<IPart>) => {
    const selected = rows.rows.filter((row) => row.getIsSelected());

    if (selected.length === 0) {
      addNotification({
        primaryText: "No parts selected",
        variant: "warn"
      });
      return;
    }

    const offerIds = selected.map((row) => row.original.offers?.[0]?.id);

    const body = {
      offerIds: offerIds
    }

    bulkWithdraw.mutate(body, bulkSaveHandlers);
  };

  const handleBulkRelist = (rows: RowModel<IPart>) => {
    const selected = rows.rows.filter((row) => row.getIsSelected());

    if (selected.length === 0) {
      addNotification({
        primaryText: "No parts selected",
        variant: "warn"
      });
      return;
    }

    const partIds = selected.map((row) => row.original.id);

    const body = {
      partIds: partIds
    }

    bulkRelist.mutate(body, bulkSaveHandlers);
  };

  const handleBulkUpdateTitle = (rows: RowModel<IPart>) => {
    const selected = rows.rows.filter((row) => row.getIsSelected());

    if (selected.length === 0) {
      addNotification({
        primaryText: "No parts selected",
        variant: "warn"
      });
      return;
    }

    const parts = selected.map((row) => row.original);

    let offerRequests: Record<string, IEbayOffer> = {};

    parts.forEach(part => {
      if (!part) return;

      const offer = part.offers?.at(0);
      if (!offer) return;

      //Create dictionary of ID and object with new title
      offer.title = computeEbayListingTitle(part, null, true);

      offerRequests[offer.id!] = offer;
    });

    const body = {
      offers: offerRequests
    };

    bulkUpdateOffer.mutate(body, bulkSaveHandlers);
  };

  const handleBulkUpdatePolicies = (rows: RowModel<IPart>, policies: IBulkUpdatePolicies) => {
    const selected = rows.rows.filter((row) => row.getIsSelected());

    if (selected.length === 0) {
      addNotification({
        primaryText: "No parts selected",
        variant: "warn"
      });
      return;
    }

    const parts = selected.map((row) => row.original);

    let offerRequests: Record<string, IEbayOffer> = {};

    parts.forEach(part => {
      if (!part) return;

      const offer = part.offers?.at(0);
      if (!offer) return;

      //Basically PATCH the policies
      if (policies.locationKey) {
        offer.locationKey = policies.locationKey;
      }
      if (policies.paymentPolicyId) {
        offer.paymentPolicyId = policies.paymentPolicyId;
      }
      if (policies.fulfillmentPolicyId) {
        offer.fulfillmentPolicyId = policies.fulfillmentPolicyId;
      }
      if (policies.returnPolicyId) {
        offer.returnPolicyId = policies.returnPolicyId;
      }

      offerRequests[offer.id!] = offer;
    });

    const body = {
      offers: offerRequests
    };

    bulkUpdateOffer.mutate(body, bulkSaveHandlers);
  };

  const handleConfirm = (onConfirm: () => void, message: string) => {
    setConfirmFunction(() => onConfirm);
    setConfirmMessage(message);
    setConfirmOpen(true);
  };

  const handleFinishedConfirm = () => {
    setConfirmOpen(false);
    setConfirmFunction(null);
    setConfirmMessage("");
  };

  const handleOpenPolicyModal = (rows: RowModel<IPart>) => {
    const selected = rows.rows.filter((row) => row.getIsSelected());

    if (selected.length === 0) {
      addNotification({
        primaryText: "No parts selected",
        variant: "warn"
      });
      return;
    }

    setSelectedRows(rows);
    reset({
      locationKey: undefined,
      paymentPolicyId: undefined,
      fulfillmentPolicyId: undefined,
      returnPolicyId: undefined,
    });
    setPolicyChangeOpen(true);
  };

  const handleOpenAspectsModal = (rows: RowModel<IPart>) => {
    const selected = rows.rows.filter((row) => row.getIsSelected());

    if (selected.length === 0) {
      addNotification({
        primaryText: "No parts selected",
        variant: "warn"
      });
      return;
    }

    setSelectedRows(rows);
    setAspectsChangeOpen(true);
  };

  const handleOpenSyncModal = (rows: RowModel<IPart>) => {
    const selected = rows.rows.filter((row) => row.getIsSelected());

    if (selected.length === 0) {
      addNotification({
        primaryText: "No parts selected",
        variant: "warn"
      });
      return;
    }

    setSelectedRows(rows);
    setSyncEbayOpen(true);
  };

  const handlePolicyFormSubmit = (data: IBulkUpdatePolicies) => {
    if (!selectedRows) return;

    // Only include fields that have values
    const policiesToUpdate: IBulkUpdatePolicies = {};
    if (data.locationKey) policiesToUpdate.locationKey = data.locationKey;
    if (data.paymentPolicyId) policiesToUpdate.paymentPolicyId = data.paymentPolicyId;
    if (data.fulfillmentPolicyId) policiesToUpdate.fulfillmentPolicyId = data.fulfillmentPolicyId;
    if (data.returnPolicyId) policiesToUpdate.returnPolicyId = data.returnPolicyId;

    // Check if at least one policy is selected
    if (Object.keys(policiesToUpdate).length === 0) {
      addNotification({
        primaryText: "Please select at least one policy to update",
        variant: "warn"
      });
      return;
    }

    handleBulkUpdatePolicies(selectedRows, policiesToUpdate);
    setPolicyChangeOpen(false);
    setSelectedRows(null);
  };

  const handleDatesChange = (newDates: DateRangeType) => {
    setDates(newDates);
  };

  useEffect(() => {
    setAdditionalBody({
      categoryId: watchPartCategory?.id,
      locationSearchMode: watchLocationSearchMode,
      warehouseLocationId: watchLocationSearchMode === "equals" ? watchWarehouseLocation?.id : undefined,
      warehouseLocationBeginsWith: watchLocationSearchMode === "beginsWith" ? watchWarehouseLocationBeginsWith || undefined : undefined,
      startDate: dates.startDate?.toISOString(),
      endDate: dates.endDate?.toISOString(),
      dateFieldType: dateFieldType,
    });
  }, [watchPartCategory, watchWarehouseLocation, watchLocationSearchMode, watchWarehouseLocationBeginsWith, dates, dateFieldType]);

  // Monitor PagedTable query state to prevent crashes
  const [queryState, setQueryState] = useState<any>(null);

  useEffect(() => {
    // Subscribe to query cache changes and find any PagedTable queries
    const unsubscribe = queryClient.getQueryCache().subscribe(() => {
      const cache = queryClient.getQueryCache();
      const pagedTableQueries = cache.getAll().filter(query =>
        query.queryKey[0] === "pagedTable"
      );

      // Get the most recent PagedTable query state
      const latestQuery = pagedTableQueries[pagedTableQueries.length - 1];
      setQueryState(latestQuery?.state || null);
    });
    return () => unsubscribe();
  }, [queryClient]);

  const isSearchDisabled = useMemo(
    () => !queryState || queryState.status === "loading" || isSearching || fetchLoading,
    [queryState, isSearching, fetchLoading]
  );

  const expandedRow = (part: IPart) => {
    return <ListingExpandedRowContent part={part} />;
  };

  return (
    <>
      <ConfirmModal open={confirmOpen} setOpen={setConfirmOpen}
        title={"Confirm changes to eBay listings?"}
        message={confirmMessage}
        confirmButtonText={"Confirm"}
        onConfirm={() => { confirmFunction?.(); handleFinishedConfirm(); }}
        isLoading={false}
      />
      <Modal open={policyChangeOpen} setOpen={setPolicyChangeOpen} width="max-w-5xl">
        <form onSubmit={handleSubmit(handlePolicyFormSubmit)}>
          <div className="space-y-4">
            <h2 className="text-lg font-medium leading-6 text-gray-900">Bulk Update eBay Listing Policies</h2>
            <p className="text-sm text-gray-600">
              Select the policies you want to update for the selected parts. Leave fields empty to keep existing values.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <SelectInput
                label={"Location of Part"}
                register={register}
                registerName={"locationKey"}
                options={locations.data?.data.locations || []}
                setValue={setValue}
                watch={watch}
              />

              <SelectInput
                label={"Shipping Policy"}
                register={register}
                registerName={"fulfillmentPolicyId"}
                options={policies.data?.data.fulfilmentPolicies || []}
                setValue={setValue}
                watch={watch}
              />

              <SelectInput
                label={"Payment Policy"}
                register={register}
                registerName={"paymentPolicyId"}
                options={policies.data?.data.paymentPolicies || []}
                setValue={setValue}
                watch={watch}
              />

              <SelectInput
                label={"Returns Policy"}
                register={register}
                registerName={"returnPolicyId"}
                options={policies.data?.data.returnPolicies || []}
                setValue={setValue}
                watch={watch}
              />
            </div>

            <div className="flex justify-end gap-4 pt-4 border-t">
              <ButtonInput
                label={"Cancel"}
                isSubmit={false}
                onClick={() => {
                  setPolicyChangeOpen(false);
                  setSelectedRows(null);
                }}
                classes={"!bg-gray-200 hover:!bg-gray-300 !text-gray-700"}
              />
              <ButtonInput
                label={"Update Policies"}
                isSubmit={true}
                onClick={undefined}
                classes={undefined}
                isLoading={bulkUpdateOffer.isLoading}
              />
            </div>
          </div>
        </form>
      </Modal>
      <Modal open={aspectsChangeOpen} setOpen={setAspectsChangeOpen} width="max-w-7xl">
        {selectedRows && (
          <BulkEditListingSpecifics
            parts={selectedRows.rows.filter((row) => row.getIsSelected()).map((row) => row.original)}
            onClose={() => {
              setAspectsChangeOpen(false);
              setSelectedRows(null);
            }}
          />
        )}
      </Modal>
      <Modal open={syncEbayOpen} setOpen={setSyncEbayOpen} width="max-w-7xl">
        {selectedRows && (
          <SyncEbayListingsModal
            parts={selectedRows.rows.filter((row) => row.getIsSelected()).map((row) => row.original)}
            onClose={() => {
              setSyncEbayOpen(false);
              setSelectedRows(null);
            }}
          />
        )}
      </Modal>
      <form className="flex flex-wrap gap-x-6 gap-y-2 items-center">
        <div className="flex">
          <ComboBoxInput
            label={"Part Category"}
            register={registerFilters}
            registerName={"partCategory"}
            options={allCategories.data?.data ?? []}
            setValue={setFilterValue}
            disabled={isSearchDisabled || allCategories.isLoading}
            searchField={"name"}
            classes="min-w-96"
            placeholder="Start Typing to Search For a Category"
          />
        </div>

        <WarehouseLocationSearch
          showSearchOptions={true}
          register={registerFilters}
          setValue={setFilterValue}
          watch={watchFilters}
          isSearchDisabled={isSearchDisabled}
          warehouseLocations={warehouseLocations}
        />

        {/* Date Range Filter */}
        <DateRangeFilter
          dates={dates}
          onDatesChange={handleDatesChange}
          dateFieldType={dateFieldType}
          onDateFieldTypeChange={setDateFieldType}
        />
      </form>

      <PagedTable
        url="/part/listings"
        pageMode={PageMode.Listed}
        columns={cols}
        queryLabel="Search part details"
        // onRowClick={(x: IPart) => openInNewTab(`ebay-listings/${x.id}`)}
        showTotal={true}
        additionalBody={additionalBody}
        expandedRow={expandedRow}
        actionButtons={[
          { label: "Update Aspects", onClick: (e: RowModel<IPart>) => handleOpenAspectsModal(e), disabledOnNoRows: true }, // Bulk update aspects
          { label: "Update Titles", onClick: (e: RowModel<IPart>) => handleConfirm(() => handleBulkUpdateTitle(e), "Are you sure you want to update the titles of the selected parts?"), isLoading: bulkUpdateOffer.isLoading, disabledOnNoRows: true }, // Working and tested
          { label: "Update Descriptions", onClick: (e: RowModel<IPart>) => handleConfirm(() => handleBulkResetDescription(e), "Are you sure you want to reset the description of the selected parts?"), isLoading: bulkUpdateProductDescription.isLoading, disabledOnNoRows: true }, // Working and tested
          { label: "Update Policies", onClick: (e: RowModel<IPart>) => handleOpenPolicyModal(e), isLoading: bulkUpdateOffer.isLoading, disabledOnNoRows: true }, //working and tested
          { label: "Bulk Relist", onClick: (e: RowModel<IPart>) => handleConfirm(() => handleBulkRelist(e), "Are you sure you want to relist the selected parts?\nThis will withdraw them from eBay then attempt to publish them."), isLoading: bulkRelist.isLoading, disabledOnNoRows: true }, //Working and tested
          { label: "Bulk Withdraw", onClick: (e: RowModel<IPart>) => handleConfirm(() => handleBulkWithdraw(e), "Are you sure you want to withdraw the selected parts?"), isLoading: bulkWithdraw.isLoading, disabledOnNoRows: true }, // Working and tested
          { label: "Sync eBay", onClick: (e: RowModel<IPart>) => handleOpenSyncModal(e), disabledOnNoRows: true },
        ]}
      />
    </>
  );
}
